import { computed } from 'vue';
import { useUserStore } from 'src/core/stores/user';
import type { UserAccess } from 'src/core/types/user';

const EUDR_PROFILE = 'EUDR';

/**
 * Profile value from a test profile entry.
 */
function getTestProfileValue(testProfile: UserAccess['test_profiles'][number]): string {
  return testProfile.profile.value;
}

/**
 * Whether the user has EUDR in their access.test_profiles.
 * When access is absent, returns true so existing behaviour is preserved.
 */
export function hasEudrInAccess(access: UserAccess | undefined): boolean {
  if (!access?.test_profiles?.length) return true;
  return access.test_profiles.some(
    (testProfile) =>
      getTestProfileValue(testProfile).toUpperCase() === EUDR_PROFILE
  );
}

/**
 * Composable for profile-based user access (access.test_profiles, access.commodities).
 * Use to gate EUDR-only UI (Parcels & DDS, ICE CoT) and to filter
 * data ingestion options (commodities, origins, test profiles).
 */
export function useUserAccess() {
  const userStore = useUserStore();

  const userAccess = computed(() => userStore.user?.access);

  const hasEudrAccess = computed(() => hasEudrInAccess(userAccess.value));

  /** Profile name values the user is allowed to use (e.g. ['EUDR', 'Data Quality', 'DCF']). */
  const allowedTestProfiles = computed(() => {
    const access = userAccess.value;
    if (!access?.test_profiles?.length) return null;
    return access.test_profiles.map((testProfile) =>
      getTestProfileValue(testProfile)
    );
  });

  /** Commodities the user is allowed to use (EnumType from API). */
  const allowedCommodities = computed(() => {
    const access = userAccess.value;
    if (!access?.commodities?.length) return null;
    return [...access.commodities].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  });

  /** Combined list of origin (country) values from all allowed test profiles. */
  const allowedOriginCountries = computed(() => {
    const access = userAccess.value;
    if (!access?.test_profiles?.length) return null;
    const countryValues = new Set<string>();
    for (const testProfile of access.test_profiles) {
      if (testProfile.countries?.length) {
        testProfile.countries.forEach((country) =>
          countryValues.add(country.value)
        );
      }
    }
    return countryValues.size ? Array.from(countryValues) : null;
  });

  return {
    userAccess,
    hasEudrAccess,
    allowedTestProfiles,
    allowedCommodities,
    allowedOriginCountries,
  };
}
